import { isNil } from 'lodash'
import React, { type FC } from 'react'
import type { BaselineDataPoint } from '@awell/ui-kit/src/components/AwellActivity/types'
import { formatDate } from '@awell/ui-kit/utils'

import { Icon } from '@awell-health/design-system'
import ShellyAvatar from '../../../../components/ShellyAvatar'
import { type Activity, ActivityAction, ActivityObjectType } from '../../types'
import { ActivitySummary } from './ActivityCard/getActivitySummary'
import { DataPointValueType } from '../../../ActivityDetails/types'
import { type Pathway } from '../../../../hooks/usePathway/types'
import { t } from 'i18next'

interface Props {
  activity: Activity
  baselineDataPoints?: Array<BaselineDataPoint>
  pathwayStatusExplanation?: string
  careflow?: Pathway
}

export const AwellActivityCard: FC<Props> = ({
  activity,
  baselineDataPoints = [],
  pathwayStatusExplanation,
  careflow
}) => {
  const { action, object, date } = activity

  const getActivityIcon = (objectType: ActivityObjectType, action: ActivityAction): React.ReactNode => {
    if (objectType === ActivityObjectType.Reminder) {
      return <Icon icon="RiNotification2Line" size={24} />
    }
    if (action === ActivityAction.Stopped) {
      return <Icon icon="RiSquareFill" size={24} />
    }
    if (action === ActivityAction.Activate) {
      return <ShellyAvatar />
    }
    return <ShellyAvatar />
  }

  const stamp =
    formatDate({
      isoDateStr: date,
      dateFormat: 'dd MMM yyyy, h:mm:ss a' // 10 Jan 2025, 5:53:00 PM
    }) ?? '-'

  const BaselineDataPointsComponent: FC<{
    objectType: ActivityObjectType
    action: ActivityAction
    baselineDataPoints: Array<BaselineDataPoint>
  }> = ({ objectType, action, baselineDataPoints }) => {
    if (
      action === ActivityAction.Activate &&
      baselineDataPoints.length > 0 &&
      objectType === ActivityObjectType.Pathway
    ) {
      const getDataPointValue = (value: string | undefined, valueType: DataPointValueType): string => {
        if (isNil(value)) {
          return '-'
        }
        if (valueType === DataPointValueType.Date) {
          const date = new Date(value)
          return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            timeZone: 'UTC'
          })
        }
        return value
      }

      return (
        <div className="mt-2">
          <div className="text-slate-700 text-sm underline mb-1">Baseline Data Points</div>
          <table>
            {baselineDataPoints.map(({ definition: { id, title, valueType }, value }) => (
              <tr key={id}>
                <td className="text-slate-400 text-sm whitespace-nowrap">{title}</td>
                <td className="px-2">
                  <Icon icon="RiArrowRightLine" size={16} className="fill-blue-400" />
                </td>
                <td className="text-slate-700 text-sm font-medium">{getDataPointValue(value as string, valueType)}</td>
              </tr>
            ))}
          </table>
        </div>
      )
    }
    return <></>
  }

  const StartedByComponent: FC<{
    careflow?: Pathway
    activityAction: ActivityAction
    objectType: ActivityObjectType
  }> = ({ careflow, activityAction, objectType }) => {
    if (activityAction === ActivityAction.Activate && objectType === ActivityObjectType.Pathway && careflow?.created) {
      const createdBy = careflow.created?.user_name ?? careflow.created?.user_email
      const createdDate = !isNil(careflow.created?.date)
        ? formatDate({ isoDateStr: careflow.created?.date, dateFormat: 'dd MMM yyyy, h:mm:ss a' })
        : careflow.start_date
      if (createdBy && createdDate) {
        return (
          <div className="text-slate-500 text-sm mb-2">
            {t('care_flow_audit_trail', { user: createdBy, date: createdDate })}
          </div>
        )
      }
    }
    return null
  }

  return (
    <div className="shadow-sm rounded-md p-6 bg-white border border-neutral-200">
      <div className="flex gap-3 items-center">
        <div className="bg-slate-100 border border-slate-200 p-1 rounded-lg">
          {getActivityIcon(object.type, action)}
        </div>
        <ActivitySummary activity={activity} careflowStopExplanation={pathwayStatusExplanation} />
      </div>
      <div className="py-2 flex justify-between overflow-auto">
        <StartedByComponent careflow={careflow} activityAction={action} objectType={object.type} />
      </div>
      {baselineDataPoints.length > 0 && (
        <div className="py-2 flex justify-between overflow-auto">
          <BaselineDataPointsComponent
            objectType={object.type}
            action={action}
            baselineDataPoints={baselineDataPoints}
          />
        </div>
      )}
      {!isNil(date) && (
        <div className="flex gap-1 justify-end items-center flex-shrink-0">
          <Icon icon="RiTimeFill" size={16} className="fill-slate-600" />
          <div className="text-neutral-500 text-sm">{stamp}</div>
        </div>
      )}
    </div>
  )
}

AwellActivityCard.displayName = 'AwellActivityCard'
